// frontend/src/views/AllViews.js
// All dashboard views. Each view:
//   1. Calls the raw-data API (no UI shaping server-side)
//   2. Pivots / filters the rows locally for each chart
//   3. Passes shaped data to ECharts wrapper components
import { useState, useMemo, useEffect, useContext, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { useApi, useMetaData } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { ThemeContext } from '../context/ThemeContext';
import { Age, Gender, Geo, Intensity, Loyalty, Competition, Social } from '../utils/api';
import { ChartCard, BarChart, LineChart, StackedBar, DualAxisBar, HeatmapChart, ChartLoader, ChartError, PickerSection } from '../components/Charts';
import { COLORS, PALETTE, INTENSITY_COLORS, LOYALTY_COLORS, fmtNum, fmtPct, fmtMonth } from '../utils/theme';
import { geoName } from '../utils/postcodes';
import { UK_MAP_NAME as MAP_NAME, POSTCODE_REGION, REGION_POSTCODES, loadUKMap } from '../utils/ukMap';
import _ from 'lodash';

// Pivot long rows → { cats: string[], series: [{name, color, data}] }
function pivotRows(rows, dimKey, valueKey = 'active_users', colorMap = null) {
  const monthOrder = _.sortedUniq(_.sortBy(rows.map(r => r.month_id)));
  const dims = _.uniq(rows.map(r => r[dimKey]));
  const byMonthDim = _.groupBy(rows, r => `${r.month_id}__${r[dimKey]}`);
  const cats = monthOrder;
  const series = dims.map((dim, i) => ({
    name:  dim,
    color: colorMap?.[dim] || PALETTE[i % PALETTE.length],
    data:  monthOrder.map(m => byMonthDim[`${m}__${dim}`]?.[0]?.[valueKey] ?? 0),
  }));
  return { cats, series };
}

// Unique app names ordered by descending total sessions — used to order the
// x-axis of the "by App" stacked charts in Customer Profiles.
function appsBySessions(rows, { exclude = [] } = {}) {
  const totals = {};
  rows.forEach(r => { totals[r.application] = (totals[r.application] || 0) + (r.total_15min_sessions || 0); });
  return _.uniq(rows.map(r => r.application))
    .filter(a => !exclude.includes(a))
    .sort((a, b) => (totals[b] || 0) - (totals[a] || 0));
}


// ══════════════════════════════════════════════════════════════════════════════
// BY AGE  (Customer Profiles — snapshot only, global category)
// ══════════════════════════════════════════════════════════════════════════════
export function ByAgeView() {
  const { category }   = useCategory();
  const meta           = useMetaData();
  const [sessionType, setSessionType] = useState('open_app');
  const { month }                     = useMonth();
  const [drillAge,    setDrillAge]    = useState(null);

  const effectiveCat = category || 'ALL';
  const selMonth     = month || meta.latestMonth;
  const p            = { category: effectiveCat, session_type: sessionType };

  const AGE_ORDER = ['18-24','25-34','35-44','45-54','55-64','Over 65','Unknown'];

  const snapshot = useApi(Age.snapshot, { ...p, month_id: selMonth }, !!selMonth);

  // Category-level totals: sum per-app rows by age (API no longer returns 'Any App' rows)
  const latestAgg = useMemo(() => {
    if (!snapshot.data) return [];
    const byAge = _.groupBy(snapshot.data, 'age');
    return AGE_ORDER
      .map(age => ({
        age,
        active_users:              _.sumBy(byAge[age] || [], 'active_users'),
        total_15min_sessions:   _.sumBy(byAge[age] || [], 'total_15min_sessions'),
      }))
      .filter(r => r.active_users > 0);
  }, [snapshot.data]);

  // Age by App: all returned rows (already excludes 'Any App' server-side)
  const { appNames: ageAppNames, ageSeries } = useMemo(() => {
    if (!snapshot.data) return { appNames: [], ageSeries: [] };
    const appNames = appsBySessions(snapshot.data);
    const ageSeries = AGE_ORDER.filter(a => snapshot.data.some(r => r.age === a)).map((age, i) => ({
      name:  age,
      color: PALETTE[i % PALETTE.length],
      data:  appNames.map(app => snapshot.data.find(r => r.application === app && r.age === age)?.active_users || 0),
    }));
    return { appNames, ageSeries };
  }, [snapshot.data]);

  // Drilldown: apps for selected age band (all rows already exclude 'Any App')
  const drillRows = useMemo(() => {
    if (!drillAge || !snapshot.data) return [];
    return _.sortBy(
      snapshot.data.filter(r => r.age === drillAge),
      r => -r.active_users
    );
  }, [drillAge, snapshot.data]);

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session:</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {meta.sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Age Distribution — dual axis: users (left) + sessions (right) */}
      <ChartCard
        title="Age Distribution — Users & Sessions"
        sub={`${effectiveCat} · ${selMonth} · click a bar to drill into apps`}
        style={{ marginBottom: 16 }}
      >
        {snapshot.loading ? <ChartLoader /> : (
          <DualAxisBar
            categories={latestAgg.map(r => r.age)}
            series1={{ name: 'Active Users',    data: latestAgg.map(r => r.active_users || 0),          color: COLORS.teal }}
            series2={{ name: 'Sessions', data: latestAgg.map(r => r.total_15min_sessions || 0), color: COLORS.indigo }}
            height={280}
            onClick={e => { const age = latestAgg[e.dataIndex]?.age; if (age) setDrillAge(prev => prev === age ? null : age); }}
          />
        )}
      </ChartCard>

      {/* Drilldown panel */}
      {drillAge && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div>
              <div className="card-title">Drilldown — {drillAge}</div>
              <div className="card-sub">Active users per app · {selMonth}</div>
            </div>
            <button className="btn-icon" onClick={() => setDrillAge(null)}>✕</button>
          </div>
          <div className="card-body">
            <BarChart
              categories={drillRows.map(r => r.application)}
              series={[{ name: 'Users', data: drillRows.map(r => r.active_users), color: COLORS.teal }]}
              height={200}
            />
          </div>
        </div>
      )}

      {/* Age by App — stacked bar */}
      <ChartCard title="Age by App" sub={`${effectiveCat} · ${selMonth}`} style={{ marginBottom: 16 }}>
        {snapshot.loading ? <ChartLoader /> : (
          <StackedBar categories={ageAppNames} series={ageSeries} height={280} normalize />
        )}
      </ChartCard>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// BY GENDER  (Customer Profiles — snapshot only, global category)
// ══════════════════════════════════════════════════════════════════════════════
export function ByGenderView() {
  const { category }   = useCategory();
  const meta           = useMetaData();
  const [sessionType, setSessionType] = useState('open_app');
  const { month }                     = useMonth();

  const effectiveCat = category || 'ALL';
  const selMonth     = month || meta.latestMonth;
  const p            = { category: effectiveCat, session_type: sessionType };

  const G_COLORS = { M: COLORS.teal, F: COLORS.indigo, U: COLORS.textMuted };
  const G_LABELS = { M: 'Male', F: 'Female', U: 'Unknown' };

  const snapshot = useApi(Gender.snapshot, { ...p, month_id: selMonth }, !!selMonth);

  // Category-level: sum users + sessions per gender across all apps
  const genderDist = useMemo(() => {
    if (!snapshot.data) return [];
    return ['M','F','U'].map(g => ({
      gender:   g,
      label:    G_LABELS[g],
      color:    G_COLORS[g],
      users:    _.sumBy(snapshot.data.filter(r => r.gender === g), 'active_users'),
      sessions: _.sumBy(snapshot.data.filter(r => r.gender === g), 'total_15min_sessions'),
    })).filter(d => d.users > 0);
  }, [snapshot.data]);

  // Gender by App stacked bar (M + F only, x = apps)
  const { appNames, appSeries } = useMemo(() => {
    if (!snapshot.data) return { appNames: [], appSeries: [] };
    const appNames = appsBySessions(snapshot.data);
    const appSeries = ['M','F','U'].map(g => ({
      name:  G_LABELS[g],
      color: G_COLORS[g],
      data:  appNames.map(app => snapshot.data.find(r => r.application===app && r.gender===g)?.active_users || 0),
    }));
    return { appNames, appSeries };
  }, [snapshot.data]);

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session:</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {meta.sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Gender Distribution — dual axis */}
      <ChartCard
        title="Gender Distribution — Users & Sessions"
        sub={`${effectiveCat} · ${selMonth}`}
        style={{ marginBottom: 16 }}
      >
        {snapshot.loading ? <ChartLoader /> : (
          <DualAxisBar
            categories={genderDist.map(d => d.label)}
            series1={{ name: 'Active Users',    data: genderDist.map(d => d.users),    color: COLORS.teal }}
            series2={{ name: 'Sessions', data: genderDist.map(d => d.sessions), color: COLORS.indigo }}
            height={260}
          />
        )}
      </ChartCard>

      {/* Gender by App — stacked bar */}
      <ChartCard title="Gender by App" sub={`${effectiveCat} · ${selMonth}`}>
        {snapshot.loading ? <ChartLoader /> : (
          <StackedBar categories={appNames} series={appSeries} height={280} normalize />
        )}
      </ChartCard>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// GEOGRAPHY  (Customer Profiles — consistent with other CP pages)
// ══════════════════════════════════════════════════════════════════════════════
export function ByGeoView() {
  const { dark }       = useContext(ThemeContext);
  const { category }   = useCategory();
  const meta           = useMetaData();
  const { month }      = useMonth();

  const [sessionType,    setSessionType]    = useState('open_app');
  const [mapReady,       setMapReady]       = useState(false);
  const [geoError,       setGeoError]       = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('Eastern');
  const [postcodeQ,      setPostcodeQ]      = useState('');
  const [selectedGeos,   setSelectedGeos]   = useState([]);
  const [geoPickerOpen,  setGeoPickerOpen]  = useState(false);
  const [tableMetric,    setTableMetric]    = useState('user_pct');
  const [tableSort,      setTableSort]      = useState({ app: null, dir: 'desc' });

  const effectiveCat = category || 'ALL';
  const selMonth     = month || meta.latestMonth;
  const p            = { category: effectiveCat, session_type: sessionType, month_id: selMonth };

  useEffect(() => { loadUKMap(ok => { setMapReady(ok); setGeoError(!ok); }); }, []);

  // All postcodes for map + table + chart filtering
  const all   = useApi(Geo.snapshot, { ...p, limit: 0 }, !!selMonth);
  // All by-app for both chart 2 and the picker
  const byApp = useApi(Geo.byApp,    { ...p, limit: 0 }, !!selMonth);

  const isUnknownGeo = geo => !geo || geo.toLowerCase() === 'unknown';

  const allRows = useMemo(
    () => (all.data || []).filter(r => !isUnknownGeo(r.geography)),
    [all.data]
  );

  // Aggregate all postcodes → region totals for map coloring
  const { regionData, maxRegionVal } = useMemo(() => {
    if (!allRows.length) return { regionData: [], maxRegionVal: 1 };
    const totals = {};
    allRows.forEach(r => {
      const region = POSTCODE_REGION[r.geography];
      if (region) totals[region] = (totals[region] || 0) + (r.active_users || 0);
    });
    const regionData   = Object.entries(totals).map(([name, value]) => ({ name, value }));
    const maxRegionVal = Math.max(1, ...regionData.map(d => d.value));
    return { regionData, maxRegionVal };
  }, [allRows]);

  // Filter snapshot rows to selected region's postcodes
  const regionRows = useMemo(() => {
    if (!selectedRegion) return allRows;
    const codes = new Set(REGION_POSTCODES[selectedRegion] || []);
    return allRows.filter(r => codes.has(r.geography));
  }, [allRows, selectedRegion]);

  // Chart 1: top 15 from region (users + sessions)
  const chart1Rows = useMemo(
    () => [...regionRows].sort((a, b) => b.active_users - a.active_users).slice(0, 15),
    [regionRows]
  );

  // Shared chart height: 34px per row + fixed overhead (legend + axes)
  // BarChart horizontal overhead ~56px, StackedBar horizontal overhead ~68px
  // Use the larger so both canvases are the same pixel height
  const drillHeight = Math.max(300, Math.min(chart1Rows.length, 15) * 34 + 68);

  // Chart 2: app breakdown — same postcode order as chart 1 (active_users desc)
  const { top10Geos, geoAppSeries } = useMemo(() => {
    const rows = (byApp.data || []).filter(r => !isUnknownGeo(r.geography));
    const inRegion = selectedRegion
      ? rows.filter(r => (REGION_POSTCODES[selectedRegion] || []).includes(r.geography))
      : rows;
    if (!inRegion.length) return { top10Geos: [], geoAppSeries: [] };
    // Derive geo order from chart1Rows (active_users desc) so both charts align
    const geos = chart1Rows.map(r => r.geography).slice(0, 15);
    const apps = _.uniq(inRegion.map(r => r.application));
    const series = apps.map((app, i) => ({
      name:  app,
      color: PALETTE[i % PALETTE.length],
      data:  geos.map(geo => inRegion.find(r => r.geography === geo && r.application === app)?.active_users || 0),
    }));
    return { top10Geos: geos, geoAppSeries: series };
  }, [byApp.data, selectedRegion, chart1Rows]);

  // Picker: all available geos from by-app
  const availableGeos = useMemo(
    () => _.uniq((byApp.data || []).filter(r => !isUnknownGeo(r.geography)).map(r => r.geography)),
    [byApp.data]
  );

  // Multi-select comparison series
  const { selGeos, selGeoAppSeries } = useMemo(() => {
    if (!byApp.data || !selectedGeos.length) return { selGeos: [], selGeoAppSeries: [] };
    const rows = byApp.data.filter(r => selectedGeos.includes(r.geography) && !isUnknownGeo(r.geography));
    const geos = selectedGeos.filter(g => rows.some(r => r.geography === g));
    const apps = _.uniq(rows.map(r => r.application));
    const series = apps.map((app, i) => ({
      name:  app,
      color: PALETTE[i % PALETTE.length],
      data:  geos.map(geo => rows.find(r => r.geography === geo && r.application === app)?.active_users || 0),
    }));
    return { selGeos: geos, selGeoAppSeries: series };
  }, [byApp.data, selectedGeos]);

  const toggleGeo = geo =>
    setSelectedGeos(prev => prev.includes(geo) ? prev.filter(g => g !== geo) : [...prev, geo]);

  // ── All-postcodes table: pivot by-app rows → one column per app ──────────────
  // Apps in the chosen category, ordered by total users (biggest competitor first)
  const tableApps = useMemo(() => {
    const totals = {};
    (byApp.data || []).filter(r => !isUnknownGeo(r.geography)).forEach(r => {
      totals[r.application] = (totals[r.application] || 0) + (r.active_users || 0);
    });
    return Object.entries(totals).sort((a, b) => b[1] - a[1]).map(([app]) => app);
  }, [byApp.data]);

  // Pivot: one row per postcode, per-app { users, sessions } + row totals
  const tableData = useMemo(() => {
    const byGeo = {};
    (byApp.data || []).filter(r => !isUnknownGeo(r.geography)).forEach(r => {
      const g = byGeo[r.geography] || (byGeo[r.geography] = {
        geography: r.geography, apps: {}, totalUsers: 0, totalSessions: 0,
      });
      g.apps[r.application] = { users: r.active_users || 0, sessions: r.total_15min_sessions || 0 };
      g.totalUsers    += r.active_users || 0;
      g.totalSessions += r.total_15min_sessions || 0;
    });
    return Object.values(byGeo).sort((a, b) => b.totalUsers - a.totalUsers);
  }, [byApp.data]);

  // Value for an app cell under the currently-selected metric
  const cellValue = (row, app) => {
    const a = row.apps[app];
    if (!a) return null;
    if (tableMetric === 'user_pct')     return row.totalUsers    ? (a.users / row.totalUsers) * 100    : 0;
    if (tableMetric === 'session_pct')  return row.totalSessions ? (a.sessions / row.totalSessions) * 100 : 0;
    return a.users ? a.sessions / a.users : 0; // sess_per_user
  };

  const filteredTableRows = useMemo(() => {
    const rows = postcodeQ
      ? tableData.filter(r =>
          r.geography.toLowerCase().includes(postcodeQ.toLowerCase()) ||
          geoName(r.geography).toLowerCase().includes(postcodeQ.toLowerCase()))
      : tableData;
    if (!tableSort.app) return rows; // default: totalUsers desc (from tableData)
    const sign = tableSort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = cellValue(a, tableSort.app), vb = cellValue(b, tableSort.app);
      // missing values always sort to the bottom regardless of direction
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return (va - vb) * sign;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, postcodeQ, tableSort, tableMetric]);

  // Click an app header: toggle asc/desc, or start a new column at desc
  const toggleTableSort = app =>
    setTableSort(prev => prev.app === app
      ? { app, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
      : { app, dir: 'desc' });
  const fmtCell = v => v == null ? '—'
    : tableMetric === 'sess_per_user' ? v.toFixed(1)
    : `${v.toFixed(1)}%`;

  // ECharts map option
  const mapOption = useMemo(() => {
    if (!mapReady) return null;
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: p => `<b>${p.name}</b><br/>Active users: <b>${fmtNum(p.value || 0)}</b>`,
        backgroundColor: dark ? '#1e2633' : '#fff',
        borderColor: dark ? '#2d3748' : '#E5E7EB',
        borderWidth: 1,
        textStyle: { color: dark ? '#F9FAFB' : '#111827', fontSize: 12 },
        extraCssText: 'box-shadow:0 4px 12px rgba(0,0,0,0.12);border-radius:8px;',
      },
      visualMap: {
        min: 0,
        max: maxRegionVal,
        text: ['High', 'Low'],
        realtime: false,
        calculable: true,
        inRange: { color: ['#E1F5EE', '#5DCAA5', '#1DB89A', '#0F6E56'] },
        textStyle: { color: dark ? '#9CA3AF' : '#6B7280', fontSize: 10 },
        right: 8,
        top: 'middle',
        itemWidth: 12,
        itemHeight: 100,
      },
      series: [{
        type: 'map',
        map: MAP_NAME,
        roam: true,
        data: regionData,
        selectedMode: 'single',
        emphasis: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        select: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        label: { show: false },
        itemStyle: {
          areaColor: dark ? '#1e2633' : '#E8ECF4',
          borderColor: dark ? '#374151' : '#fff',
          borderWidth: 0.8,
        },
      }],
    };
  }, [mapReady, regionData, maxRegionVal, dark]);

  const onMapClick = useCallback(e => {
    const region = e?.name;
    if (!region) return;
    setSelectedRegion(prev => prev === region ? null : region);
  }, []);

  return (
    <div>
      {/* ── Filter bar ─────────────────────────────────────────────── */}
      <div className="filters-bar" style={{ flexWrap: 'wrap' }}>
        <span className="filter-label">Session:</span>
        <select className="filter-select" value={sessionType} onChange={e => { setSessionType(e.target.value); setSelectedRegion(null); }}>
          {meta.sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {selectedRegion && (
          <button
            onClick={() => setSelectedRegion(null)}
            style={{ fontSize: 12, fontWeight: 600, color: '#EF4444', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.20)', borderRadius: 100, padding: '4px 12px', cursor: 'pointer' }}
          >
            ✕ {selectedRegion}
          </button>
        )}
      </div>

      {/* ── Map + charts grid ──────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: selectedRegion ? '1fr 1fr 1fr' : '1fr',
        gap: 16,
        alignItems: 'stretch',
        marginBottom: 16,
      }}>
        {/* Choropleth map */}
        <ChartCard
          title="Active Users by Region"
          sub={`${effectiveCat} · ${fmtMonth(selMonth)} · click a region to explore its postcodes`}
        >
          {geoError && <ChartError message="Failed to load UK map" height={520} />}
          {!geoError && (all.loading || !mapReady) && <ChartLoader height={520} />}
          {!geoError && mapReady && !all.loading && mapOption && (
            regionData.length > 0 ? (
              <ReactECharts
                key={selectedRegion ?? 'full'}
                echarts={echarts}
                option={mapOption}
                style={{ height: 520 }}
                onEvents={{ click: onMapClick }}
                notMerge
              />
            ) : <div className="empty-state" style={{ height: 520 }}>No data</div>
          )}
        </ChartCard>

        {/* Chart 1: Active Users — horizontal bar, top 15 postcodes in region */}
        {selectedRegion && (
          <ChartCard
            title={`Active Users — ${selectedRegion}`}
            sub={`${effectiveCat} · ${fmtMonth(selMonth)} · top 15 postcodes by users`}
          >
            {all.loading ? <ChartLoader height={drillHeight} /> :
             chart1Rows.length === 0
               ? <div className="empty-state" style={{ height: drillHeight }}>No data for this region</div>
               : <BarChart
                   categories={chart1Rows.slice(0, 15).map(r => geoName(r.geography))}
                   series={[
                     { name: 'Active Users', color: COLORS.teal,   data: chart1Rows.slice(0, 15).map(r => r.active_users) },
                     { name: 'Sessions',     color: COLORS.indigo, data: chart1Rows.slice(0, 15).map(r => r.total_15min_sessions) },
                   ]}
                   horizontal
                   xFormatter={v => fmtNum(v)}
                   height={drillHeight}
                 />
            }
          </ChartCard>
        )}

        {/* Chart 2: App market share breakdown — normalised stacked bar */}
        {selectedRegion && (
          <ChartCard
            title={`App Market Share — ${selectedRegion}`}
            sub={`${effectiveCat} · ${fmtMonth(selMonth)} · top 15 postcodes · normalised`}
          >
            {byApp.loading ? <ChartLoader height={drillHeight} /> :
             top10Geos.length === 0
               ? <div className="empty-state" style={{ height: drillHeight }}>No app data for this region</div>
               : <StackedBar
                   categories={top10Geos.map(geoName)}
                   series={geoAppSeries}
                   height={drillHeight}
                   normalize
                   horizontal
                 />
            }
          </ChartCard>
        )}
      </div>

      {!selectedRegion && !all.loading && regionData.length > 0 && (
        <div style={{ textAlign: 'center', marginBottom: 16, color: 'var(--text-secondary)', fontSize: 12 }}>
          Click a region on the map to see postcode-level users and app breakdown
        </div>
      )}

      {/* ── All postcodes table — one column per app ─────────────────── */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div className="card-title">All Postcodes</div>
              <div className="card-sub">
                {filteredTableRows.length !== tableData.length
                  ? `${filteredTableRows.length} of ${tableData.length} areas`
                  : `${tableData.length} areas`}
              </div>
            </div>
            <input
              className="geo-search-input"
              type="text"
              placeholder="Search postcode…"
              value={postcodeQ}
              onChange={e => setPostcodeQ(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="filter-label">Metric:</span>
            <select className="filter-select" value={tableMetric} onChange={e => setTableMetric(e.target.value)}>
              <option value="user_pct">User %</option>
              <option value="session_pct">Session %</option>
              <option value="sess_per_user">Sessions / User</option>
            </select>
          </div>
        </div>
        <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Postcode</th>
                {tableApps.map(app => (
                  <th
                    key={app}
                    onClick={() => toggleTableSort(app)}
                    title={`Sort by ${app}`}
                    style={{ textAlign: 'right', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
                  >
                    {app}
                    <span style={{ marginLeft: 4, opacity: tableSort.app === app ? 1 : 0.25 }}>
                      {tableSort.app === app && tableSort.dir === 'asc' ? '▲' : '▼'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredTableRows.map((r, i) => (
                <tr key={r.geography} className={i % 2 ? 'alt' : ''}>
                  <td style={{ color: COLORS.textMuted }}>{i + 1}</td>
                  <td><strong>{geoName(r.geography)}</strong></td>
                  {tableApps.map(app => (
                    <td key={app} style={{ textAlign: 'right' }}>{fmtCell(cellValue(r, app))}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Compare selected postcodes by App ──────────────────────── */}
      <PickerSection
        callout={{
          title: 'Compare Selected Postcodes by App',
          subtitle: 'Choose postcode areas below to see how app market share differs across locations',
          highlights: [
            { label: 'Full area access', desc: 'Choose any postcode area in the data, not just the top chart selections.' },
            { label: 'App breakdown', desc: 'See how market share between apps differs across your selected postcodes.' },
            { label: 'Normalised view', desc: '100% stacked bars make cross-area share comparison effortless.' },
          ],
        }}
        style={{ marginTop: 16 }}
      >
        <div className="card-header">
          <div>
            <div className="card-title">Compare Selected Postcodes by App</div>
            <div className="card-sub">
              {selectedGeos.length > 0
                ? `${selectedGeos.length} postcode${selectedGeos.length > 1 ? 's' : ''} selected`
                : 'Select postcodes below to compare'}
            </div>
          </div>
          <button
            className="btn-icon"
            style={{ width: 'auto', padding: '0 10px', fontSize: 11, gap: 4 }}
            onClick={() => setGeoPickerOpen(o => !o)}
          >
            {geoPickerOpen ? '▲' : '▼'} Choose Postcodes
          </button>
          {selectedGeos.length > 0 && (
            <button className="btn-icon" style={{ marginLeft: 6 }} onClick={() => setSelectedGeos([])}>✕ Clear</button>
          )}
        </div>
        {geoPickerOpen && (
          <div className="geo-picker">
            {availableGeos.map(geo => (
              <label key={geo} className={`geo-picker-item ${selectedGeos.includes(geo) ? 'selected' : ''}`}>
                <input type="checkbox" checked={selectedGeos.includes(geo)} onChange={() => toggleGeo(geo)} style={{ marginRight: 6 }} />
                {geoName(geo)}
              </label>
            ))}
          </div>
        )}
        {selectedGeos.length > 0 && selGeoAppSeries.length > 0 && (
          <div className="card-body">
            <StackedBar categories={selGeos.map(geoName)} series={selGeoAppSeries} height={260} normalize />
          </div>
        )}
        {selectedGeos.length > 0 && selGeoAppSeries.length === 0 && !byApp.loading && (
          <div className="empty-state" style={{ height: 80 }}>No app data for selected postcodes</div>
        )}
      </PickerSection>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// INTENSITY  (Customer Profiles — snapshot only, global category)
// ══════════════════════════════════════════════════════════════════════════════
export function IntensityView() {
  const { category }   = useCategory();
  const meta           = useMetaData();
  const [sessionType, setSessionType] = useState('open_app');
  const { month }                     = useMonth();

  const effectiveCat = category || 'ALL';
  const selMonth     = month || meta.latestMonth;
  const p            = { category: effectiveCat, session_type: sessionType };

  const LEVELS = ['Light','Medium','Heavy'];

  const snapshot = useApi(Intensity.snapshot, { ...p, month_id: selMonth }, !!selMonth);

  // Category-level intensity distribution — 'Any App' rows are the deduplicated totals
  const intensityDist = useMemo(() => {
    if (!snapshot.data) return [];
    const anyApp = snapshot.data.filter(r => r.application === 'Any App');
    return LEVELS.map(lvl => ({
      level:    lvl,
      color:    INTENSITY_COLORS[lvl],
      users:    _.sumBy(anyApp.filter(r => r.intensity === lvl), 'active_users'),
      sessions: _.sumBy(anyApp.filter(r => r.intensity === lvl), 'total_15min_sessions'),
    })).filter(d => d.users > 0);
  }, [snapshot.data]);

  // Intensity by App stacked bar — real apps only ('Any App' feeds the distribution above)
  const { appNames, appSeries } = useMemo(() => {
    if (!snapshot.data) return { appNames: [], appSeries: [] };
    const appNames = appsBySessions(snapshot.data, { exclude: ['Any App'] });
    const appSeries = LEVELS.map(lvl => ({
      name:  lvl,
      color: INTENSITY_COLORS[lvl],
      data:  appNames.map(app => snapshot.data.find(r => r.application===app && r.intensity===lvl)?.active_users || 0),
    }));
    return { appNames, appSeries };
  }, [snapshot.data]);

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session:</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {meta.sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Intensity Distribution — dual axis */}
      <ChartCard
        title="Intensity Distribution — Users & Sessions"
        sub={`${effectiveCat} · ${selMonth}`}
        style={{ marginBottom: 16 }}
      >
        {snapshot.loading ? <ChartLoader /> : (
          <DualAxisBar
            categories={intensityDist.map(d => d.level)}
            series1={{ name: 'Active Users',    data: intensityDist.map(d => d.users),    color: COLORS.teal }}
            series2={{ name: 'Sessions', data: intensityDist.map(d => d.sessions), color: COLORS.amber }}
            height={260}
          />
        )}
      </ChartCard>

      {/* Intensity by App — stacked bar */}
      <ChartCard title="Intensity by App" sub={`${effectiveCat} · ${selMonth}`}>
        {snapshot.loading ? <ChartLoader /> : (
          <StackedBar categories={appNames} series={appSeries} height={280} normalize />
        )}
      </ChartCard>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// LOYALTY  (Customer Profiles — snapshot only, global category)
// ══════════════════════════════════════════════════════════════════════════════
export function LoyaltyView() {
  const { category }   = useCategory();
  const meta           = useMetaData();
  const [sessionType, setSessionType] = useState('open_app');
  const { month }                     = useMonth();

  const effectiveCat = category || 'ALL';
  const selMonth     = month || meta.latestMonth;
  const p            = { category: effectiveCat, session_type: sessionType };

  const LEVELS = ['Solus','Dual','Three+'];

  const snapshot = useApi(Loyalty.snapshot, { ...p, month_id: selMonth }, !!selMonth);

  // Category-level loyalty distribution
  const loyaltyDist = useMemo(() => {
    if (!snapshot.data) return [];
    return LEVELS.map(lvl => ({
      level:    lvl,
      color:    LOYALTY_COLORS[lvl],
      users:    _.sumBy(snapshot.data.filter(r => r.app_loyalty === lvl), 'active_users'),
      sessions: _.sumBy(snapshot.data.filter(r => r.app_loyalty === lvl), 'total_15min_sessions'),
    })).filter(d => d.users > 0);
  }, [snapshot.data]);

  // Loyalty by App stacked bar
  const { appNames, appSeries } = useMemo(() => {
    if (!snapshot.data) return { appNames: [], appSeries: [] };
    const appNames = appsBySessions(snapshot.data);
    const appSeries = LEVELS.map(lvl => ({
      name:  lvl,
      color: LOYALTY_COLORS[lvl],
      data:  appNames.map(app => snapshot.data.find(r => r.application===app && r.app_loyalty===lvl)?.active_users || 0),
    }));
    return { appNames, appSeries };
  }, [snapshot.data]);

  // ── App-loyalty trend: pick a segment + one or more apps, plot 6 months ──────
  const [loyaltySeg, setLoyaltySeg] = useState('Solus');
  const [selApps,    setSelApps]    = useState(() => new Set());

  const appTrend = useApi(Loyalty.appTrend, p, !!selMonth);

  const trendApps = useMemo(
    () => (appTrend.data ? appsBySessions(appTrend.data) : []),
    [appTrend.data],
  );
  // Default to the top 3 apps by sessions until the user picks their own.
  const chosenApps = useMemo(() => {
    const chosen = [...selApps].filter(a => trendApps.includes(a));
    return chosen.length ? chosen : trendApps.slice(0, 3);
  }, [selApps, trendApps]);

  const trendMonths = useMemo(() => {
    const ms = [...new Set((appTrend.data || []).map(r => r.month_id))].sort();
    return ms.filter(m => !selMonth || m <= selMonth).slice(-6);
  }, [appTrend.data, selMonth]);

  const appTrendSeries = useMemo(() => {
    if (!appTrend.data) return [];
    return chosenApps.map((app, i) => {
      const byMonth = Object.fromEntries(
        appTrend.data.filter(r => r.application === app && r.app_loyalty === loyaltySeg)
          .map(r => [r.month_id, r.active_users]),
      );
      return { name: app, color: PALETTE[i % PALETTE.length], data: trendMonths.map(m => byMonth[m] ?? null) };
    });
  }, [appTrend.data, chosenApps, loyaltySeg, trendMonths]);

  const toggleApp = app => setSelApps(prev => {
    const next = new Set(prev.size ? prev : chosenApps);
    next.has(app) ? next.delete(app) : next.add(app);
    return next;
  });

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session:</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {meta.sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Loyalty Distribution — dual axis */}
      <ChartCard
        title="App Loyalty Distribution — Users & Sessions"
        sub={`${effectiveCat} · ${selMonth}`}
        style={{ marginBottom: 16 }}
      >
        {snapshot.loading ? <ChartLoader /> : (
          <DualAxisBar
            categories={loyaltyDist.map(d => d.level)}
            series1={{ name: 'Active Users',    data: loyaltyDist.map(d => d.users),    color: COLORS.teal }}
            series2={{ name: 'Sessions', data: loyaltyDist.map(d => d.sessions), color: COLORS.purple }}
            height={260}
          />
        )}
      </ChartCard>

      {/* Loyalty by App — stacked bar */}
      <ChartCard title="Loyalty by App" sub={`${effectiveCat} · ${selMonth}`} style={{ marginBottom: 16 }}>
        {snapshot.loading ? <ChartLoader /> : (
          <StackedBar categories={appNames} series={appSeries} height={280} normalize />
        )}
      </ChartCard>

      {/* App-loyalty trend — segment filter + multi-app picker */}
      <ChartCard
        title={`App Loyalty Trend — ${loyaltySeg} users`}
        sub={`${effectiveCat} · ${sessionType} · ${chosenApps.length} app${chosenApps.length === 1 ? '' : 's'} · last 6 months`}
      >
        <div className="filters-bar" style={{ marginBottom: 10 }}>
          <span className="filter-label">Loyalty:</span>
          <select className="filter-select" value={loyaltySeg} onChange={e => setLoyaltySeg(e.target.value)}>
            {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="fa-compare-picker">
          {trendApps.map(app => (
            <label key={app} className={`fa-chip ${chosenApps.includes(app) ? 'active' : ''}`}>
              <input type="checkbox" checked={chosenApps.includes(app)} onChange={() => toggleApp(app)} />
              {app}
            </label>
          ))}
        </div>
        {appTrend.loading ? <ChartLoader />
          : appTrendSeries.length > 0
            ? <LineChart categories={trendMonths.map(fmtMonth)} series={appTrendSeries} height={300} />
            : <div className="empty-state">No data</div>}
      </ChartCard>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// COMPETITION — Duplication Matrix + Share of Requirements
// (global category; shared overlap data via useOverlapMatrices)
// ══════════════════════════════════════════════════════════════════════════════
// SoR heatmap summary columns (rendered directly beside the app matrix)
const SOR_TOTAL_COL   = 'Total Sessions';
const SOR_NOT_WON_COL = '% Not Won';

function useOverlapMatrices() {
  const { category }   = useCategory();
  const meta           = useMetaData();
  const [sessionType, setSessionType] = useState('open_app');
  const { month }                     = useMonth();

  const effectiveCat = category || 'ALL';
  const selMonth     = month || meta.latestMonth;
  const p            = { category: effectiveCat, session_type: sessionType, month_id: selMonth };

  const overlap = useApi(Competition.overlap, p, !!selMonth);

  // Penetration per app: active_users_x / active_users_category
  const penetrationMap = useMemo(() => {
    if (!overlap.data?.length) return {};
    const map = {};
    overlap.data.forEach(r => {
      if (!map[r.application_x] && r.active_users_category) {
        map[r.application_x] = +(r.active_users_x / r.active_users_category * 100).toFixed(2);
      }
    });
    return map;
  }, [overlap.data]);

  // Build apps list, user-overlap heatmap ("All Apps" row/col), and SoR heatmap
  const { apps, heatData, sorData, sorRedData, sorDenom, rawApps } = useMemo(() => {
    if (!overlap.data?.length) return { apps: [], heatData: [], sorData: [], sorRedData: [], sorDenom: {}, rawApps: [] };

    const rawApps = _.uniq([
      ...overlap.data.map(r => r.application_x),
      ...overlap.data.map(r => r.application_y),
    ]).sort();

    const ALL = 'All Apps';
    // Empty spacer category = blank row/column separating 'All Apps' from real apps
    const apps = [...rawApps, '', ALL];

    // ── User overlap matrix ──────────────────────────────────────────────────
    const heatData = overlap.data.map(r => [r.application_y, r.application_x, r.overlap_pct]);
    // Diagonal = 100%
    rawApps.forEach(app => heatData.push([app, app, 100]));
    // "All Apps" row: penetration of each column app (% of category users who use it)
    rawApps.forEach(app => heatData.push([app, ALL, penetrationMap[app] ?? null]));
    // "All Apps" column: penetration of each row app
    rawApps.forEach(app => heatData.push([ALL, app, penetrationMap[app] ?? null]));
    heatData.push([ALL, ALL, 100]);

    // ── Sessions SoR matrix ──────────────────────────────────────────────────
    // SoR(row X, col Y) = X-users' sessions in app Y ÷ X-users' total category
    // sessions. Denominator = own-app sessions (sessions_x) + their sessions in
    // every other app (Σ sessions_xy) — each row's shares sum to 100%.
    const ownSessions = {};
    const sorDenom    = {};
    overlap.data.forEach(r => {
      if (!(r.application_x in sorDenom)) {
        ownSessions[r.application_x] = r.total_15min_sessions_x || 0;
        sorDenom[r.application_x]    = r.total_15min_sessions_x || 0;
      }
      sorDenom[r.application_x] += r.total_15min_sessions_xy || 0;
    });

    const sorPct = (num, x) =>
      sorDenom[x] ? +(num / sorDenom[x] * 100).toFixed(2) : null;

    const sorData = overlap.data.map(r => [
      r.application_y,
      r.application_x,
      sorPct(r.total_15min_sessions_xy, r.application_x),
    ]);
    // Diagonal = share of the app's users' category sessions spent in the app itself
    rawApps.forEach(app => sorData.push([app, app, sorPct(ownSessions[app], app)]));
    // Summary columns beside the matrix (no All Apps row on this heatmap):
    //  Total Sessions — row-app users' category sessions (absolute; 0 in the
    //                   % slot keeps the cell on the palest colour)
    //  % Not Won      — 100 − own-app share, spaced apart on a red ramp
    const sorRedData = [];
    rawApps.forEach(app => {
      sorData.push([SOR_TOTAL_COL, app, 0, sorDenom[app] ?? null]);
      const own = sorPct(ownSessions[app], app);
      sorRedData.push([SOR_NOT_WON_COL, app, own != null ? +(100 - own).toFixed(2) : null]);
    });

    return { apps, heatData, sorData, sorRedData, sorDenom, rawApps };
  }, [overlap.data, penetrationMap]);

  const heatHeight = Math.max(340, apps.length * 44 + 120);
  const sorHeight  = Math.max(340, (rawApps?.length || 0) * 44 + 120);

  return {
    meta, sessionType, setSessionType, effectiveCat, selMonth,
    overlap, apps, rawApps, heatData, sorData, sorRedData, sorDenom, heatHeight, sorHeight,
  };
}

function OverlapFilters({ meta, sessionType, setSessionType }) {
  return (
    <div className="filters-bar">
      <span className="filter-label">Session:</span>
      <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
        {meta.sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
  );
}

// ── Shared guide banner + drawer (used by both Competition and SoR views) ──────
function SharedCustomersGuideBanner({ onOpen }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '12px 18px', marginBottom: 20, gap: 12,
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
          How Brands Grow — Shared Customers Guide
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Byron Sharp's Duplication of Purchase Law, Double Jeopardy, and how to translate shared-customer data into growth decisions.
        </div>
      </div>
      <button
        onClick={onOpen}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'var(--teal, #1DB89A)', color: '#fff',
          fontSize: 12, fontWeight: 600, padding: '7px 14px',
          borderRadius: 7, border: 'none', cursor: 'pointer',
          whiteSpace: 'nowrap', flexShrink: 0,
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
        </svg>
        Open Guide
      </button>
    </div>
  );
}

function SharedCustomersGuideDrawer({ onClose }) {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          zIndex: 1000, backdropFilter: 'blur(2px)',
        }}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(780px, 60vw)',
        background: '#fff', zIndex: 1001,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.18)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>How Brands Grow — Shared Customers Guide</span>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, padding: '2px 6px',
          }}>✕</button>
        </div>
        <iframe
          src="/shared-customers-guide.html"
          title="Shared Customers Guide"
          style={{ flex: 1, border: 'none', width: '100%' }}
        />
      </div>
    </>
  );
}

export function CompetitionView() {
  const m = useOverlapMatrices();
  const { meta, sessionType, setSessionType, effectiveCat, selMonth, overlap, apps, heatData, heatHeight } = m;
  const [guideOpen, setGuideOpen] = useState(false);

  const userTooltip = p =>
    p.data[0] === 'All Apps' || p.data[1] === 'All Apps'
      ? `<b>${p.data[0] === 'All Apps' ? p.data[1] : p.data[0]}</b> — category penetration<br/>` +
        `<b>${p.data[2] != null ? p.data[2] + '%' : '—'}</b> of category users`
      : `<b>${p.data[1]}</b> users who also use <b>${p.data[0]}</b><br/>` +
        `Overlap: <b>${p.data[2] != null ? p.data[2] + '%' : '—'}</b>`;

  return (
    <div>
      {/* ── Guide banner ─────────────────────────────────────────────────── */}
      <SharedCustomersGuideBanner onOpen={() => setGuideOpen(true)} />
      {guideOpen && <SharedCustomersGuideDrawer onClose={() => setGuideOpen(false)} />}

      <OverlapFilters meta={meta} sessionType={sessionType} setSessionType={setSessionType} />

      {/* User Overlap Heatmap */}
      <ChartCard
        title="Duplication Matrix — User Overlap %"
        sub={`${effectiveCat} · ${selMonth} · % of row-app users who also use column-app · All Apps row/col = category penetration`}
        style={{ marginBottom: 16 }}
      >
        {overlap.loading ? <ChartLoader height={heatHeight} /> : (
          apps.length > 0
            ? <HeatmapChart key={apps.length} apps={apps} data={heatData} height={heatHeight} tooltipFormatter={userTooltip} emphasize="All Apps" />
            : <div className="empty-state">No overlap data for selected filters</div>
        )}
      </ChartCard>

      {/* Detail table — user overlap only (Session SoR lives in Share of Requirements) */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Full Overlap Detail</div>
          <div className="card-sub">All pairwise combinations · sorted by user overlap %</div>
        </div>
        <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>App (row)</th>
                <th>Also uses (column)</th>
                <th style={{ textAlign: 'right' }}>Shared Users</th>
                <th style={{ textAlign: 'right' }}>Row App Users</th>
                <th style={{ textAlign: 'right' }}>User Overlap %</th>
              </tr>
            </thead>
            <tbody>
              {(overlap.data || []).map((r, i) => (
                <tr key={`${r.application_x}-${r.application_y}`} className={i % 2 ? 'alt' : ''}>
                  <td><strong>{r.application_x}</strong></td>
                  <td>{r.application_y}</td>
                  <td style={{ textAlign: 'right' }}>{fmtNum(r.active_users_xy)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtNum(r.active_users_x)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <span className={`pill ${r.overlap_pct > 50 ? 'pill-red' : r.overlap_pct > 25 ? 'pill-amber' : 'pill-teal'}`}>
                      {fmtPct(r.overlap_pct)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function ShareOfRequirementsView() {
  const m = useOverlapMatrices();
  const { meta, sessionType, setSessionType, effectiveCat, selMonth, overlap, rawApps, sorData, sorRedData, sorDenom, sorHeight } = m;
  const [guideOpen, setGuideOpen] = useState(false);

  // Spacer between Total Sessions and the red % Not Won column
  const sorXApps = [...rawApps, SOR_TOTAL_COL, '', SOR_NOT_WON_COL];

  // ── % Sessions Lost trend (6 months ending at the global month) ──────────
  const [trendApps,      setTrendApps]      = useState([]);
  const [trendPickerOpen, setTrendPickerOpen] = useState(false);
  const sorTrend = useApi(
    () => Competition.sorTrend({ category: effectiveCat, session_type: sessionType }),
    { effectiveCat, sessionType },
  );
  const trendMonths = useMemo(() => {
    const all = [...new Set((sorTrend.data || []).map(r => r.month_id))].sort();
    return all.filter(mo => !selMonth || mo <= selMonth).slice(-6);
  }, [sorTrend.data, selMonth]);
  const trendAppOptions = useMemo(
    () => [...new Set((sorTrend.data || []).map(r => r.application))].sort(),
    [sorTrend.data],
  );
  const trendSeries = useMemo(() => {
    if (!sorTrend.data) return [];
    return trendApps.map((app, i) => {
      const byMonth = Object.fromEntries(
        sorTrend.data.filter(r => r.application === app).map(r => [r.month_id, r.pct_sessions_lost]),
      );
      return { name: app, color: PALETTE[i % PALETTE.length], data: trendMonths.map(mo => byMonth[mo] ?? null) };
    });
  }, [sorTrend.data, trendApps, trendMonths]);
  const toggleTrendApp = app =>
    setTrendApps(prev => prev.includes(app) ? prev.filter(a => a !== app) : [...prev, app]);

  const sorTooltip = p =>
    p.data[0] === SOR_TOTAL_COL
      ? `<b>${p.data[1]}</b> users — total category sessions<br/>` +
        `<b>${p.data[3] != null ? fmtNum(p.data[3]) : '—'}</b> sessions across all apps`
      : p.data[0] === SOR_NOT_WON_COL
        ? `<b>${p.data[1]}</b> — sessions not won<br/>` +
          `<b>${p.data[2] != null ? p.data[2] + '%' : '—'}</b> of its users' category sessions happen in other apps`
        : `<b>${p.data[1]}</b> users' sessions in <b>${p.data[0]}</b><br/>` +
          `Share of Requirements: <b>${p.data[2] != null ? p.data[2] + '%' : '—'}</b> of their category sessions`;

  // Pairwise rows: SoR = row-app users' sessions in column app ÷ their total
  // category sessions. Largest first.
  const sorRows = useMemo(() => {
    return (overlap.data || [])
      .map(r => {
        const denom = sorDenom[r.application_x];
        return {
          ...r,
          denom,
          sor: denom ? +(r.total_15min_sessions_xy / denom * 100).toFixed(1) : null,
        };
      })
      .sort((a, b) => (b.sor ?? -1) - (a.sor ?? -1));
  }, [overlap.data, sorDenom]);

  return (
    <div>
      {/* ── Guide banner ─────────────────────────────────────────────────── */}
      <SharedCustomersGuideBanner onOpen={() => setGuideOpen(true)} />
      {guideOpen && <SharedCustomersGuideDrawer onClose={() => setGuideOpen(false)} />}

      <OverlapFilters meta={meta} sessionType={sessionType} setSessionType={setSessionType} />

      {/* Share of Requirements — Sessions Heatmap */}
      <ChartCard
        title="Share of Requirements — Sessions %"
        sub={`${effectiveCat} · ${selMonth} · % of row-app users' category sessions spent in the column app (rows sum to 100%) · Total Sessions = row users' category total · % Not Won = usage happening outside the app`}
        style={{ marginBottom: 16 }}
      >
        {overlap.loading ? <ChartLoader height={sorHeight} /> : (
          rawApps.length > 0
            ? <HeatmapChart
                key={rawApps.length}
                apps={rawApps}
                xApps={sorXApps}
                yApps={rawApps}
                data={sorData}
                redData={sorRedData}
                height={sorHeight}
                tooltipFormatter={sorTooltip}
                emphasize={[SOR_TOTAL_COL, SOR_NOT_WON_COL]}
              />
            : <div className="empty-state">No session data for selected filters</div>
        )}
      </ChartCard>

      {/* % Sessions Lost — per-app trend with app picker */}
      <PickerSection
        callout={{
          title: "% Sessions Lost — 6-Month Trend",
          subtitle: "Select apps below to track how much category usage they lose to competitors over time",
          highlights: [
            { label: "Multi-app tracking", desc: "Follow session loss trends for multiple apps simultaneously over 6 months." },
            { label: "Competitive signals", desc: "Spot which brands are gaining or losing user time vs the competition." },
            { label: "Time series view", desc: "See seasonal patterns and trend shifts in how engaged users split their time." },
          ],
        }}
        style={{ marginBottom: 16 }}
      >
        <div className="card-header">
          <div>
            <div className="card-title">% Sessions Lost — 6-Month Trend</div>
            <div className="card-sub">
              {trendApps.length > 0
                ? `${trendApps.length} app${trendApps.length > 1 ? 's' : ''} · share of each app's users' category sessions happening in other apps · 6 months ending ${fmtMonth(selMonth)}`
                : "Select apps below to see how much of their users' category usage they lose to other apps"}
            </div>
          </div>
          <button
            className="btn-icon"
            style={{ width: "auto", padding: "0 10px", fontSize: 11, gap: 4 }}
            onClick={() => setTrendPickerOpen(o => !o)}
          >
            {trendPickerOpen ? "▲" : "▼"} Choose Apps
          </button>
          {trendApps.length > 0 && (
            <button className="btn-icon" style={{ marginLeft: 6 }} onClick={() => setTrendApps([])}>✕</button>
          )}
        </div>

        {trendPickerOpen && (
          <div className="geo-picker">
            {trendAppOptions.map(app => (
              <label key={app} className={`geo-picker-item ${trendApps.includes(app) ? "selected" : ""}`}>
                <input
                  type="checkbox"
                  checked={trendApps.includes(app)}
                  onChange={() => toggleTrendApp(app)}
                  style={{ marginRight: 6 }}
                />
                {app}
              </label>
            ))}
          </div>
        )}

        {sorTrend.error && <ChartError message={sorTrend.error} height={120} />}
        {trendApps.length > 0 && !sorTrend.loading && !sorTrend.error && trendSeries.length > 0 && (
          <div className="card-body">
            <LineChart
              categories={trendMonths.map(fmtMonth)}
              series={trendSeries}
              height={320}
              pointLabels={v => `${v}%`}
            />
          </div>
        )}
        {trendApps.length > 0 && sorTrend.loading && <ChartLoader height={320} />}
      </PickerSection>

      {/* Detail table — session SoR */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Share of Requirements Detail</div>
          <div className="card-sub">All pairwise combinations · sorted by session SoR %</div>
        </div>
        <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>App users (row)</th>
                <th>Sessions in (column)</th>
                <th style={{ textAlign: 'right' }}>Sessions in Column App</th>
                <th style={{ textAlign: 'right' }}>Users' Category Sessions</th>
                <th style={{ textAlign: 'right' }}>Session SoR %</th>
              </tr>
            </thead>
            <tbody>
              {sorRows.map((r, i) => (
                <tr key={`${r.application_x}-${r.application_y}`} className={i % 2 ? 'alt' : ''}>
                  <td><strong>{r.application_x}</strong></td>
                  <td>{r.application_y}</td>
                  <td style={{ textAlign: 'right' }}>{fmtNum(r.total_15min_sessions_xy)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtNum(r.denom)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {r.sor != null ? (
                      <span className={`pill ${r.sor > 50 ? 'pill-red' : r.sor > 25 ? 'pill-amber' : 'pill-teal'}`}>
                        {fmtPct(r.sor)}
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// SOCIAL
// ══════════════════════════════════════════════════════════════════════════════

// Align two reach datasets by channel; sorted by ref reach_pct desc, extras appended.
// reach_pct = active_users_xy / total_app_users (own-user-base denominator).
function alignReachChannels(refRows, otherRows) {
  const key       = r => r.channel_y;
  const refSorted = [...refRows].sort((a, b) => (b.reach_pct || 0) - (a.reach_pct || 0));
  const refChs    = refSorted.map(key);
  const extraChs  = otherRows.map(key).filter(c => !refChs.includes(c));
  const channels  = [...refChs, ...extraChs];
  const refMap    = Object.fromEntries(refRows.map(r => [key(r),   r.reach_pct || 0]));
  const otherMap  = Object.fromEntries(otherRows.map(r => [key(r), r.reach_pct || 0]));
  return {
    channels,
    refData:   channels.map(c => refMap[c]   || 0),
    otherData: channels.map(c => otherMap[c] || 0),
  };
}

// Signed gap in reach percentage points: otherRows.reach_pct − refRows.reach_pct
function reachGap(refRows, otherRows, channels) {
  const aMap = Object.fromEntries(refRows.map(r => [r.channel_y, r.reach_pct || 0]));
  const bMap = Object.fromEntries(otherRows.map(r => [r.channel_y, r.reach_pct || 0]));
  return channels.map(ch => ({
    channel_y: ch,
    gap: +((bMap[ch] || 0) - (aMap[ch] || 0)).toFixed(1),
  }));
}

// Grouped reach bar + signed gap chart for two datasets
function ChannelReachComparison({ nameA, nameB, dataA, dataB, colorA, colorB, loading, error }) {
  const rowsA = dataA || [];
  const rowsB = dataB || [];

  const { channels, refData, otherData } = useMemo(
    () => alignReachChannels(rowsA, rowsB), [rowsA, rowsB],
  );
  const gapRows = useMemo(() => reachGap(rowsA, rowsB, channels), [rowsA, rowsB, channels]);

  const barH = Math.max(220, channels.length * 36 + 60);
  const gapH = Math.max(220, gapRows.length * 36 + 60);

  if (loading) return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}><ChartLoader /><ChartLoader /></div>;
  if (error)   return <ChartError message={error} />;
  if (!rowsA.length && !rowsB.length) return <div className="empty-state">No social reach data for this selection</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
      <ChartCard
        title="Channel Reach — % of Users"
        sub={`${nameA} vs ${nameB} — % of each group's users active on each platform`}
      >
        <BarChart
          categories={channels}
          series={[
            { name: nameA, color: colorA, data: refData },
            { name: nameB, color: colorB, data: otherData },
          ]}
          horizontal
          xFormatter={v => `${v}%`}
          height={barH}
        />
      </ChartCard>
      <ChartCard
        title={`${nameB} vs ${nameA} — Reach Gap`}
        sub={`Positive = ${nameB} over-indexes on this channel vs ${nameA} (percentage points)`}
      >
        <BarChart
          categories={gapRows.map(r => r.channel_y)}
          series={[{
            name: 'Gap (pp)',
            data: gapRows.map(r => ({
              value: r.gap,
              itemStyle: { color: r.gap >= 0 ? COLORS.positive : COLORS.negative },
            })),
          }]}
          horizontal
          xFormatter={v => `${v > 0 ? '+' : ''}${v} pp`}
          height={gapH}
        />
      </ChartCard>
    </div>
  );
}

// Opportunity Index: category_reach × (1 − brand_reach / category_reach)
// = category_reach − brand_reach (the uncovered category reach per channel)
// Sorted descending — highest = most untapped potential.
function OpportunityIndex({ catData, brandData, brandName, loading, error }) {
  const rows = useMemo(() => {
    if (!catData?.length) return [];
    const brandMap = Object.fromEntries((brandData || []).map(r => [r.channel_y, r.reach_pct || 0]));
    return catData
      .map(r => {
        const cat   = r.reach_pct || 0;
        const brand = brandMap[r.channel_y] || 0;
        const score = +(cat - brand).toFixed(1);
        const idx   = cat > 0 ? Math.round((brand / cat) * 100) : 0;
        return { channel_y: r.channel_y, cat_reach: cat, brand_reach: brand, brand_index: idx, score };
      })
      .sort((a, b) => b.score - a.score);
  }, [catData, brandData]);

  if (loading) return <ChartLoader />;
  if (error)   return <ChartError message={error} />;
  if (!rows.length) return <div className="empty-state">No data for this selection</div>;

  const barH = Math.max(220, rows.length * 36 + 60);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
      <ChartCard
        title="Opportunity Score by Channel"
        sub={`Category reach − ${brandName} reach — higher = more untapped category buyers`}
      >
        <BarChart
          categories={rows.map(r => r.channel_y)}
          series={[{
            name: 'Opportunity Score',
            data: rows.map(r => ({
              value: r.score,
              itemStyle: { color: r.score >= 5 ? COLORS.teal : r.score >= 0 ? COLORS.blue : COLORS.negative },
            })),
          }]}
          horizontal
          xFormatter={v => `${v > 0 ? '+' : ''}${v} pp`}
          height={barH}
        />
      </ChartCard>
      <ChartCard
        title="Channel Summary"
        sub={`Category reach, ${brandName} reach, and brand coverage index per platform`}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Channel', 'Category Reach', `${brandName} Reach`, 'Brand Index', 'Opportunity'].map(h => (
                  <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Channel' ? 'left' : 'right', color: 'var(--text-secondary)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.channel_y} style={{ borderBottom: '1px solid var(--border-subtle)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-subtle)' }}>
                  <td style={{ padding: '6px 10px', fontWeight: 500 }}>{r.channel_y}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{r.cat_reach.toFixed(1)}%</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{r.brand_reach.toFixed(1)}%</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: r.brand_index >= 100 ? COLORS.positive : COLORS.amber }}>{r.brand_index}%</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: r.score > 0 ? COLORS.teal : COLORS.negative }}>{r.score > 0 ? '+' : ''}{r.score} pp</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}

const NARRATIVE_STYLE = {
  fontSize: 13,
  lineHeight: 1.65,
  color: 'var(--text-secondary)',
  marginBottom: 16,
  maxWidth: 820,
};

export function SocialView() {
  const meta         = useMetaData();
  const { category } = useCategory();
  const { month }    = useMonth();
  const selMonth     = month || meta.latestMonth;

  const appsReq  = useApi(Social.applications, { category }, !!category);
  const brandList = useMemo(() => (appsReq.data || []).map(r => r.application), [appsReq.data]);

  const [sessionType, setSessionType] = useState('open_app');
  const { sessionTypes } = meta;

  const [brand, setBrand] = useState('');
  const selBrand = brandList.includes(brand) ? brand : (brandList[0] || '');

  const [cmpA, setCmpA] = useState('');
  const [cmpB, setCmpB] = useState('');
  const selCmpA = brandList.includes(cmpA) ? cmpA : (brandList[0] || '');
  const selCmpB = brandList.includes(cmpB) ? cmpB : (brandList[1] || '');

  const reachParams = (application) => ({
    month_id: selMonth, category, application, session_type: sessionType,
  });

  const catReq   = useApi(Social.reach, reachParams('ALL Universe'), !!selMonth && !!category);
  const brandReq = useApi(Social.reach, reachParams(selBrand),        !!selMonth && !!category && !!selBrand);
  const cmpAReq  = useApi(Social.reach, reachParams(selCmpA),          !!selMonth && !!category && !!selCmpA);
  const cmpBReq  = useApi(Social.reach, reachParams(selCmpB),          !!selMonth && !!category && !!selCmpB);

  const [guideOpen, setGuideOpen] = useState(false);

  return (
    <div>

      {/* ── Guide callout ────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center',
        background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.28)',
        borderRadius: 10, padding: '14px 16px', marginBottom: 20,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M9 18h6M10 22h4M12 2a7 7 0 0 1 4 12.74V17a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.26A7 7 0 0 1 12 2z"/>
        </svg>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#B45309', marginBottom: 2 }}>Channel Planning Guide — 2 sections on this page</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Covers <strong>Brand vs Category</strong> and <strong>Compare Brands</strong> — Byron Sharp framework, how to read each chart, and how to act on the gaps.
          </div>
        </div>
        <button
          onClick={() => setGuideOpen(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
            background: '#F59E0B', color: '#fff',
            fontSize: 12, fontWeight: 600, padding: '7px 14px',
            borderRadius: 7, border: 'none', cursor: 'pointer',
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
          </svg>
          Open Guide
        </button>
      </div>

      {/* ── Right-side guide drawer ───────────────────────────────────────── */}
      {guideOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setGuideOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
              zIndex: 1000, backdropFilter: 'blur(2px)',
            }}
          />
          {/* Panel */}
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: 'min(780px, 60vw)',
            background: '#fff', zIndex: 1001,
            display: 'flex', flexDirection: 'column',
            boxShadow: '-8px 0 40px rgba(0,0,0,0.18)',
          }}>
            {/* Panel header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', borderBottom: '1px solid var(--border)',
              background: '#fff', flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 7,
                  background: 'var(--teal, #1DB89A)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                  </svg>
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                  Channel Planning Guide
                </span>
              </div>
              <button
                onClick={() => setGuideOpen(false)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: 6, borderRadius: 6, color: 'var(--text-secondary)',
                  display: 'flex', alignItems: 'center',
                }}
                title="Close guide"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            {/* iframe */}
            <iframe
              src="/social-channel-guide.html"
              title="Channel Planning Guide"
              style={{ flex: 1, border: 'none', width: '100%' }}
            />
          </div>
        </>
      )}

      {/* ── Session type filter ───────────────────────────────────────────── */}
      <div className="filters-bar" style={{ marginBottom: 24 }}>
        <span className="filter-label">Session Type</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {(sessionTypes || []).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* ══ Section 1: Brand vs Category ════════════════════════════════════ */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
        paddingBottom: 12, borderBottom: '2px solid var(--border)',
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, flexShrink: 0,
          background: COLORS.teal, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800, fontSize: 15,
        }}>1</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Brand vs Category — Social Channel Reach</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 1 }}>
            Compare how your brand reaches category buyers across social platforms
          </div>
        </div>
      </div>
      <p style={NARRATIVE_STYLE}>
        From a Byron Sharp perspective, the primary benchmark for channel planning is <strong>category buyers</strong> —
        not the general population. This chart shows what percentage of brand buyers vs. all category buyers are active
        on each social platform. A platform where the brand's reach is lower than the category average signals an
        under-coverage gap: potential buyers are present but the brand is not proportionally represented.
      </p>

      {/* Brand pill picker — Section 1 */}
      {brandList.length > 0 && (
        <div style={{
          background: 'rgba(29,184,154,0.06)', border: '1px solid rgba(29,184,154,0.18)',
          borderRadius: 12, padding: '14px 18px', marginBottom: 16,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.teal, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Choose a brand to compare vs category
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {brandList.map((a, i) => {
              const active = a === selBrand;
              return (
                <button key={a} onClick={() => setBrand(a)} style={{
                  padding: '6px 16px', borderRadius: 100,
                  border: active ? `2px solid ${PALETTE[i % PALETTE.length]}` : '1.5px solid #E5E7EB',
                  background: active ? PALETTE[i % PALETTE.length] : 'transparent',
                  color: active ? '#fff' : '#374151',
                  fontWeight: active ? 700 : 500, fontSize: 13, cursor: 'pointer',
                  transition: 'all 0.15s', whiteSpace: 'nowrap',
                }}>{a}</button>
              );
            })}
          </div>
        </div>
      )}

      <ChannelReachComparison
        nameA="Category (All)"
        nameB={selBrand || '—'}
        dataA={catReq.data}
        dataB={brandReq.data}
        colorA={COLORS.teal}
        colorB={COLORS.blue}
        loading={catReq.loading || brandReq.loading}
        error={catReq.error || brandReq.error}
      />

      {/* ══ Section 2: Compare Brands ════════════════════════════════════════ */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 28,
        paddingBottom: 12, borderBottom: '2px solid var(--border)',
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, flexShrink: 0,
          background: COLORS.indigo, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800, fontSize: 15,
        }}>2</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Compare Brands — Social Channel Reach</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 1 }}>
            Head-to-head social reach across platforms for any two brands
          </div>
        </div>
      </div>
      <p style={NARRATIVE_STYLE}>
        Sharp argues that brand buyers tend to mirror the category — so large differences in social channel reach
        between two brands can reveal whether one brand is drawing from a narrower or skewed slice of the category.
        A channel where Brand B significantly out-reaches Brand A may signal either a stronger social investment or
        a structural difference in their respective buyer profiles.
      </p>

      {/* Dual brand pill pickers — Section 2 */}
      {brandList.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          {/* Brand A */}
          <div style={{
            background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.20)',
            borderRadius: 12, padding: '14px 18px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.indigo, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Brand A
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {brandList.map(a => {
                const active = a === selCmpA;
                return (
                  <button key={a} onClick={() => setCmpA(a)} style={{
                    padding: '5px 14px', borderRadius: 100,
                    border: active ? `2px solid ${COLORS.indigo}` : '1.5px solid #E5E7EB',
                    background: active ? COLORS.indigo : 'transparent',
                    color: active ? '#fff' : '#374151',
                    fontWeight: active ? 700 : 500, fontSize: 13, cursor: 'pointer',
                    transition: 'all 0.15s', whiteSpace: 'nowrap',
                  }}>{a}</button>
                );
              })}
            </div>
          </div>
          {/* Brand B */}
          <div style={{
            background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.22)',
            borderRadius: 12, padding: '14px 18px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.amber, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Brand B
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {brandList.map(a => {
                const active = a === selCmpB;
                return (
                  <button key={a} onClick={() => setCmpB(a)} style={{
                    padding: '5px 14px', borderRadius: 100,
                    border: active ? `2px solid ${COLORS.amber}` : '1.5px solid #E5E7EB',
                    background: active ? COLORS.amber : 'transparent',
                    color: active ? '#fff' : '#374151',
                    fontWeight: active ? 700 : 500, fontSize: 13, cursor: 'pointer',
                    transition: 'all 0.15s', whiteSpace: 'nowrap',
                  }}>{a}</button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <ChannelReachComparison
        nameA={selCmpA || '—'}
        nameB={selCmpB || '—'}
        dataA={cmpAReq.data}
        dataB={cmpBReq.data}
        colorA={COLORS.indigo}
        colorB={COLORS.amber}
        loading={cmpAReq.loading || cmpBReq.loading}
        error={cmpAReq.error || cmpBReq.error}
      />
    </div>
  );
}

export function OpportunityIndexByChannelView() {
  const meta         = useMetaData();
  const { category } = useCategory();
  const { month }    = useMonth();
  const selMonth     = month || meta.latestMonth;

  const appsReq  = useApi(Social.applications, { category }, !!category);
  const brandList = useMemo(() => (appsReq.data || []).map(r => r.application), [appsReq.data]);

  const [sessionType, setSessionType] = useState('open_app');
  const { sessionTypes } = meta;

  const [brand, setBrand] = useState('');
  const selBrand = brandList.includes(brand) ? brand : (brandList[0] || '');

  const reachParams = (application) => ({
    month_id: selMonth, category, application, session_type: sessionType,
  });

  const catReq   = useApi(Social.reach, reachParams('ALL Universe'), !!selMonth && !!category);
  const brandReq = useApi(Social.reach, reachParams(selBrand),        !!selMonth && !!category && !!selBrand);

  const [guideOpen, setGuideOpen] = useState(false);

  return (
    <div>
      {/* Guide drawer */}
      {guideOpen && (
        <>
          <div onClick={() => setGuideOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, backdropFilter: 'blur(2px)' }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(780px, 60vw)', background: '#fff', zIndex: 1001, display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Channel Planning Guide</span>
              <button onClick={() => setGuideOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center', color: 'var(--text-secondary)' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <iframe src="/social-channel-guide.html#s2" title="Channel Planning Guide" style={{ flex: 1, border: 'none', width: '100%' }} />
          </div>
        </>
      )}

      {/* ── Brand selector ───────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(29,184,154,0.06) 0%, rgba(29,184,154,0.02) 100%)',
        border: '1px solid rgba(29,184,154,0.25)',
        borderRadius: 12, padding: '18px 20px', marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--teal, #1DB89A)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="8" r="5"/><path d="M3 21v-1a8 8 0 0 1 16 0v1"/>
            </svg>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--teal, #1DB89A)', textTransform: 'uppercase', letterSpacing: '.07em' }}>
              Select Brand
            </span>
          </div>
          <button
            onClick={() => setGuideOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
              background: 'var(--teal, #1DB89A)', color: '#fff',
              fontSize: 12, fontWeight: 600, padding: '6px 14px',
              borderRadius: 7, border: 'none', cursor: 'pointer',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </svg>
            Open Guide
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {appsReq.loading
            ? <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '6px 0' }}>Loading brands…</div>
            : brandList.map(a => (
              <button
                key={a}
                onClick={() => setBrand(a)}
                style={{
                  padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: selBrand === a ? 700 : 400,
                  border: selBrand === a ? '2px solid var(--teal, #1DB89A)' : '1.5px solid var(--border)',
                  background: selBrand === a ? 'rgba(29,184,154,0.12)' : 'var(--bg-card, #fff)',
                  color: selBrand === a ? 'var(--teal, #1DB89A)' : 'var(--text)',
                  cursor: 'pointer', transition: 'all .15s ease',
                  boxShadow: selBrand === a ? '0 0 0 3px rgba(29,184,154,0.12)' : 'none',
                }}
              >
                {a}
              </button>
            ))
          }
        </div>
      </div>

      {/* Session type filter */}
      <div className="filters-bar" style={{ marginBottom: 20 }}>
        <span className="filter-label">Session Type</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {(sessionTypes || []).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Definition + Formula side-by-side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20, alignItems: 'stretch' }}>

        {/* Left — concise definition */}
        <div style={{
          background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '16px 20px', fontSize: 13, lineHeight: 1.65,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 10 }}>
            What it measures
          </div>
          <p style={{ margin: '0 0 10px', color: 'var(--text)' }}>
            Ranks channels by untapped category reach — how many category buyers the brand is <em>not</em> yet covering.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--teal, #1DB89A)', fontWeight: 700, flexShrink: 0 }}>Opportunity Score &gt; 0</span>
              <span style={{ color: 'var(--text-secondary)' }}>— brand under-reaches category buyers on this channel</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ color: '#94a3b8', fontWeight: 600, flexShrink: 0 }}>Score &lt; 0</span>
              <span style={{ color: 'var(--text-secondary)' }}>— brand over-indexes vs. the category</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ color: '#f59e0b', fontWeight: 700, flexShrink: 0 }}>Brand Index &lt; 100</span>
              <span style={{ color: 'var(--text-secondary)' }}>— brand reach below category parity</span>
            </div>
          </div>
        </div>

        {/* Right — Byron Sharp formula */}
        <div style={{
          background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '16px 20px',
          fontFamily: '"SF Mono", "Fira Code", "Fira Mono", monospace',
          fontSize: 12.5, lineHeight: 2,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>
            Byron Sharp formula
          </div>
          <div>
            <span style={{ color: 'var(--teal, #1DB89A)', fontWeight: 700 }}>Opportunity Index</span>
            <span style={{ color: 'var(--text-secondary)' }}> = </span>
            <span>
              (<span style={{ borderBottom: '1px solid var(--text)' }}>Cat. Reach</span> / <span style={{ borderBottom: '1px solid var(--text)' }}>Pop. Reach</span>)
              {' '}×{' '}
              (1 − <span style={{ borderBottom: '1px solid var(--text)' }}>Brand Reach</span> / <span style={{ borderBottom: '1px solid var(--text)' }}>Cat. Reach</span>)
            </span>
          </div>
          <div style={{ borderTop: '1px dashed var(--border)', margin: '8px 0' }} />
          <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontFamily: 'inherit', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Dashboard simplification (Pop. Reach constant across channels)
          </div>
          <div>
            <span style={{ color: 'var(--teal, #1DB89A)', fontWeight: 700 }}>Opportunity Score</span>
            <span style={{ color: 'var(--text-secondary)' }}> = </span>
            <span>Category Reach %</span>
            <span style={{ color: 'var(--text-secondary)' }}> − </span>
            <span>Brand Reach %</span>
          </div>
          <div style={{ borderTop: '1px dashed var(--border)', margin: '8px 0' }} />
          <div>
            <span style={{ color: '#f59e0b', fontWeight: 700 }}>Brand Index</span>
            <span style={{ color: 'var(--text-secondary)' }}> = </span>
            <span>(Brand Reach / Cat. Reach)</span>
            <span style={{ color: 'var(--text-secondary)' }}> × 100</span>
          </div>
        </div>
      </div>

      <OpportunityIndex
        catData={catReq.data}
        brandData={brandReq.data}
        brandName={selBrand || '—'}
        loading={catReq.loading || brandReq.loading}
        error={catReq.error || brandReq.error}
      />
    </div>
  );
}